import { ApiService } from '@supply-mover-2-0/core';
import type {
	ListUserNotificationsResponseDto,
	NotificationMutationResponseDto,
	NotificationPreferencesDto,
	UpdateNotificationPreferencesDto,
	WebPushPublicKeyDto,
	WebPushSubscriptionDto,
} from '@supply-mover-2-0/shared';

/**
 * Browser client for the per-user notification bell surface
 * (`/api/notifications/me/*`, DD.9.5.1). Every method targets the
 * calling user's OWN notifications — the server resolves the user from
 * context, so no userId is ever sent.
 *
 * Paths omit the `/api` prefix that `ApiService` prepends (mirrors
 * `SoftphoneSelfServiceQueryService`).
 */
export class NotificationQueryService {
	/** The bell list — unread-first, not dismissed, not expired. */
	public static async list(args?: {
		limit?: number;
		offset?: number;
	}): Promise<ListUserNotificationsResponseDto> {
		const params = new URLSearchParams();
		if (args?.limit != null) params.append('limit', String(args.limit));
		if (args?.offset != null) params.append('offset', String(args.offset));
		const qs = params.toString();
		return ApiService.apiGet<ListUserNotificationsResponseDto>(
			`/notifications/me${qs !== '' ? `?${qs}` : ''}`,
		);
	}

	/** Unread count for the red badge — polled while the tray is closed. */
	public static async getUnreadCount(): Promise<{ count: number }> {
		return ApiService.apiGet<{ count: number }>(
			'/notifications/me/unread-count',
		);
	}

	/** Mark one notification read (fired when the user clicks the row). */
	public static async markRead(
		notificationId: number,
	): Promise<NotificationMutationResponseDto> {
		return ApiService.apiPost<NotificationMutationResponseDto>(
			`/notifications/me/${notificationId}/mark-read`,
			{},
		);
	}

	/** Mark every visible unread notification read (clears the badge). */
	public static async markAllRead(): Promise<NotificationMutationResponseDto> {
		return ApiService.apiPost<NotificationMutationResponseDto>(
			'/notifications/me/mark-all-read',
			{},
		);
	}

	/** Dismiss one notification — remove it from the tray entirely. */
	public static async dismiss(
		notificationId: number,
	): Promise<NotificationMutationResponseDto> {
		return ApiService.apiPost<NotificationMutationResponseDto>(
			`/notifications/me/${notificationId}/dismiss`,
			{},
		);
	}

	/**
	 * The calling user's notification preferences (PD-14290) — master
	 * pause switch, per-category channel matrix, and per-type push
	 * overrides, with system defaults already applied.
	 */
	public static async getPreferences(): Promise<NotificationPreferencesDto> {
		return ApiService.apiGet<NotificationPreferencesDto>(
			'/notifications/me/preferences',
		);
	}

	/** Update the calling user's preferences; returns the full resolved set. */
	public static async updatePreferences(
		body: UpdateNotificationPreferencesDto,
	): Promise<NotificationPreferencesDto> {
		return ApiService.apiPut<NotificationPreferencesDto>(
			'/notifications/me/preferences',
			body,
		);
	}

	/**
	 * The server's VAPID public key (PD-14301) — fed to
	 * `PushManager.subscribe` so the browser can encrypt push payloads to
	 * the server's key pair. Empty string when push isn't configured.
	 */
	public static async getVapidPublicKey(): Promise<WebPushPublicKeyDto> {
		return ApiService.apiGet<WebPushPublicKeyDto>(
			'/notifications/me/push/vapid-public-key',
		);
	}

	/** Register this browser's push subscription for the calling user. */
	public static async subscribe(
		body: WebPushSubscriptionDto,
	): Promise<NotificationMutationResponseDto> {
		return ApiService.apiPost<NotificationMutationResponseDto>(
			'/notifications/me/push/subscribe',
			body,
		);
	}

	/** Remove this browser's push subscription (identified by endpoint). */
	public static async unsubscribe(
		endpoint: string,
	): Promise<NotificationMutationResponseDto> {
		return ApiService.apiPost<NotificationMutationResponseDto>(
			'/notifications/me/push/unsubscribe',
			{ endpoint },
		);
	}
}
